// Initiative Roadmap reports (BETA) — API client.
// Backend: app/routes/roadmap_reports.py (/api/admin/roadmap/reports), a
// roadmap-scoped view over the shared reports store. Single-report metadata +
// the standalone HTML document are served by the existing signals endpoints
// (the report viewer is report-type-agnostic), so reads of one report reuse
// signalsReportsApi.

import api from './api';
import type { SignalReport, ReportRunQueued } from './signalsReports';

export interface RoadmapReportListResponse {
  items: SignalReport[];
  total: number;
  limit: number;
  offset: number;
}

export interface RoadmapReportListParams {
  q?: string;
  limit?: number;
  offset?: number;
}

// Mirrors services/roadmap/contract.py (RoadmapStructure v3) — the
// machine-readable roadmap a Gantt-style view renders.
export type InitiativeStatus =
  | 'proposed'
  | 'in_progress'
  | 'blocked'
  | 'waiting'
  // Standing work with no end date; its sub-actions carry the dates (contract v7).
  | 'ongoing'
  | 'parked'
  | 'done'
  | 'dropped'
  | 'unknown';

// Mirrors services/roadmap/health.py — deterministic schedule classification.
export type ScheduleHealth =
  | 'on_track'
  | 'late'
  | 'at_risk'
  | 'stuck'
  | 'no_target'
  | 'parked'
  | 'done'
  | 'dropped';

// Contract v3 (2026-09-08 check-in): a sub-action of an initiative — the
// timeline row expands into these, each with its own people and date.
export type ActionStatus = 'open' | 'in_progress' | 'done' | 'dropped' | 'unknown';

export interface InitiativeAction {
  key: string; // stable slug within its initiative (derived from the title when the agent gave none)
  title: string;
  people: string[];
  status: ActionStatus;
  started_on: string | null; // ISO date — only when stated
  target_date: string | null; // ISO date — only when stated
  completed_on: string | null; // ISO date — only when reported done
  depends_on: string[]; // keys of sibling actions this one waits on
  sources: string[];
  note: string;
}

export type InitiativePriority = 'top' | 'high' | 'normal' | 'low';

// One dated decision the group made about an initiative (contract v4).
export interface InitiativeDecision {
  on: string | null; // ISO date, when stated
  what: string;
  who: string[];
  source: string;
}

// One quoted excerpt behind an initiative or one of its questions (contract
// v8, kanban 9eb5f005). The 2026-09-28 check-in could not tell what a question
// was about from two meeting ids: this is the words, who said them, and where
// they open. `label` / `source_date` name the meeting when the id resolved;
// `source_url` is the deep link the page reads (`/roadmap?source=<kind>:<id>`),
// null for a document or knowledge-bank entry the page names but cannot open.
export interface InitiativeEvidence {
  source_kind: RoadmapSourceKind;
  source_id: string;
  quote: string;
  said_by: string | null;
  on: string | null; // ISO date, when stated
  for_questions: string[];
  for_actions: string[];
  label: string | null;
  source_date: string | null; // ISO date
  resolved_id: string | null;
  source_url: string | null;
}

export interface RoadmapInitiative {
  key: string;
  title: string;
  owner: string | null;
  people: string[];
  owner_team?: string | null;
  related_teams?: string[];
  teams: string[];
  status: InitiativeStatus;
  depends_on: string[];
  related_to: string[];
  started_on: string | null; // ISO date
  target_date: string | null; // ISO date
  summary: string;
  sources: string[];
  open_questions: string[];
  actions: InitiativeAction[];
  priority?: InitiativePriority | null;
  external_blocker?: string | null;
  parked_reason?: string | null;
  decisions?: InitiativeDecision[];
}

export interface RoadmapStructure {
  v: number;
  initiatives: RoadmapInitiative[];
  open_questions: string[];
  unplaced: string[];
}

// Durable current state (roadmap_initiatives) — what the Gantt view renders.
// `people` is agent-given or derived from `owner` server-side; `blocks` is the
// reverse of depends_on across the tracked set; `health` as of the request.
// An admin's pinned team assignment (kanban ae374351): set on /roadmap, it
// outranks whatever the roadmap agent submits for that initiative on every
// later run, until an admin releases it.
export interface TeamsOverride {
  /** v9: the one owning team (null = "no task force owns it") and the related ones. */
  owner_team: string | null;
  related_teams: string[];
  /** Derived: owner first, then related. */
  teams: string[];
  /** v10: the pinned mandate area (null = the pin leaves the filing to Merlin). */
  mandate_area?: string | null;
  previous_teams: string[];
  set_by_email: string;
  set_at: string;
}

// An admin's park (kanban cac323f9): set on /roadmap with a reason, it keeps
// the initiative parked on every later roadmap run until an admin un-parks
// it, which hands back `previous_status`.
export interface ParkOverride {
  parked_reason: string | null;
  previous_status: InitiativeStatus;
  set_by_email: string;
  set_at: string;
}

export interface InitiativeState {
  initiative_key: string;
  title: string;
  owner: string | null;
  people: string[];
  /** v9 (owner ruling 2026-10-12): ONE owning team, the rest only related —
   * being discussed in a team's meeting does not make the item theirs.
   * `teams` is the derived union (owner first) every older reader uses. */
  owner_team?: string | null;
  related_teams?: string[];
  teams: string[];
  /** v10 (kanban 838512f8): the owning team's mandate area this work serves —
   * the key ('other' = the team's work outside every area; null = not filed
   * yet) and its title from the registry. */
  mandate_area?: string | null;
  mandate_area_title?: string | null;
  teams_override?: TeamsOverride | null;
  park_override?: ParkOverride | null;
  status: InitiativeStatus;
  health: ScheduleHealth;
  depends_on: string[];
  blocks: string[];
  related_to: string[];
  started_on: string | null;
  target_date: string | null;
  summary: string;
  sources: string[];
  open_questions: string[];
  actions: InitiativeAction[];
  priority: InitiativePriority | null;
  external_blocker: string | null;
  // Contract v6: why a parked initiative is sidelined, as stated.
  parked_reason?: string | null;
  decisions: InitiativeDecision[];
  // Contract v8: the quoted excerpts behind the row and its questions.
  evidence?: InitiativeEvidence[];
  last_report_id: string | null;
  first_seen_at: string;
  updated_at: string;
  event_count: number;
}

export interface InitiativeStateListResponse {
  items: InitiativeState[];
  total: number;
}

export interface InitiativeEvent {
  id: string;
  // 'edited' = a human changed it on /roadmap (report_id null, actor set).
  kind: 'created' | 'updated' | 'edited';
  changes: Record<string, { from: unknown; to: unknown }>;
  report_id: string | null;
  created_at: string;
  actor?: string | null;
}

// What PUT /admin/roadmap/initiatives/{key}/teams returns.
export interface InitiativeTeamsResult {
  initiative_key: string;
  owner_team: string | null;
  related_teams: string[];
  teams: string[];
  mandate_area?: string | null;
  mandate_area_title?: string | null;
  changed: boolean;
  override: TeamsOverride;
}

/** The body of PUT /admin/roadmap/initiatives/{key}/teams (v9; v10 adds the area). */
export interface TeamAssignmentInput {
  owner_team: string | null;
  related_teams: string[];
  /** v10: the owner's mandate area to file it under (omit / null = leave that to Merlin). */
  mandate_area?: string | null;
}

// ── Team mandates (kanban 838512f8; GET/PUT /admin/roadmap/mandates) ─────────

export interface MandateArea {
  key: string;
  title: string;
  description: string;
  /** Active initiatives filed under this area. */
  initiatives: number;
}

export interface TeamMandate {
  team: string;
  /** The team's high-level definition in the group's words (may span lines);
   * null = not written yet. The ONE copy (kanban 16a22d06): the team view,
   * the Quality door and Merlin's kickoff all read it. */
  definition: string | null;
  /** The team's charter verbatim (Ravi Joshi and PJ Murphy's "Team Charters",
   * kanban 87ab737b): the whole text Merlin is handed every run; null = none
   * on record. */
  charter: string | null;
  areas: MandateArea[];
  basis: string | null;
  set_by_email: string;
  set_at: string;
  /** Active initiatives owned by the team but not filed under any area yet. */
  unfiled: number;
  /** Active initiatives filed under the reserved 'other' key. */
  outside: number;
  revisions: number;
}

export interface TeamMandateListResponse {
  teams: TeamMandate[];
  other_key: string;
}

export interface MandateAreaInput {
  key?: string | null;
  title: string;
  description?: string;
}

export interface TeamMandateRequest {
  areas: MandateAreaInput[];
  basis?: string | null;
  /** Omitted = keep the stored definition; null or "" = clear it. */
  definition?: string | null;
  /** Omitted = keep the stored charter; null or "" = clear it. */
  charter?: string | null;
}

// ── Relevance marks on Merlin's questions (kanban 3709db93) ─────────────────

export type RelevanceVerdict = 'relevant' | 'not_relevant';

export interface RelevanceMark {
  by: string;
  verdict: RelevanceVerdict;
  at: string;
}

/** The pilot group's marks on one question, and the reader's own. */
export interface QuestionRelevance {
  relevant: number;
  not_relevant: number;
  mine: RelevanceVerdict | null;
  marks: RelevanceMark[];
}

export interface RelevanceMarkRequest extends QuestionIdentity {
  verdict: RelevanceVerdict;
}

export interface RelevanceBucket {
  label: string;
  relevant: number;
  not_relevant: number;
  /** Distinct questions the marks cover. */
  questions: number;
  /** Share marked relevant, 0..1; null with no marks. */
  rate: number | null;
  report_id?: string | null;
  report_date?: string | null;
}

export interface RelevanceSummary {
  overall: RelevanceBucket;
  /** Oldest run first. */
  by_run: RelevanceBucket[];
  by_team: RelevanceBucket[];
  by_reader: RelevanceBucket[];
  open_questions: number;
  unmarked: number;
  target_rate: number;
}

// What PUT / DELETE /admin/roadmap/initiatives/{key}/park return.
export interface InitiativeParkResult {
  initiative_key: string;
  status: InitiativeStatus;
  parked_reason: string | null;
  changed: boolean;
  override: ParkOverride | null;
}

export interface InitiativeHistoryResponse {
  initiative_key: string;
  events: InitiativeEvent[];
}

// One row of the roadmap run ledger (roadmap_runs) — GET /admin/roadmap/runs.
// 'skipped': a weekday schedule tick that found nothing new to read and spent
// nothing (kanban 916935f1); detail.skip carries the guard's counts.
export type RoadmapRunState = 'queued' | 'running' | 'published' | 'skipped' | 'failed';
export type RoadmapRunFailedStep =
  | 'lost'
  | 'dispatch'
  | 'agent'
  | 'not_published'
  | 'persist';

export interface RoadmapRun {
  id: string;
  state: RoadmapRunState;
  trigger: 'manual' | 'schedule';
  triggered_by_email: string | null;
  attempts: number;
  queued_at: string;
  started_at: string | null;
  heartbeat_at: string | null;
  finished_at: string | null;
  model: string | null;
  window_start: string | null;
  meeting_ids: string[];
  gap_count: number | null;
  audit_conversation_id: string | null;
  report_id: string | null;
  tool_calls: number | null;
  failed_step: RoadmapRunFailedStep | null;
  error: string | null;
  // Ledger rows the run stamped, by outcome (absent on runs before the ledger).
  source_counts?: Record<string, number>;
  // Verbatim bookkeeping (agent-console response, notice outcome, skip decision).
  detail?: Record<string, unknown>;
}

export interface RoadmapRunQueued extends ReportRunQueued {
  run: RoadmapRun;
  created: boolean;
}

// One applied change across ALL initiatives, stamped with the run (report)
// that applied it — GET /admin/roadmap/events (history across runs).
export interface RoadmapEvent extends InitiativeEvent {
  initiative_key: string;
  title: string;
  report_title: string | null;
  report_date: string | null; // ISO date
}

export interface RoadmapEventListResponse {
  events: RoadmapEvent[];
  total: number;
}

export interface RoadmapEventListParams {
  /** Only events recorded at or after this ISO-8601 instant. */
  since?: string;
  /** Only events whose changes touch this field (e.g. 'status'). */
  field?: string;
}

// The admin's pending in-app answer or dismissal of one question (kanban
// 816b31c3): shown on the card until the next roadmap run consumes it.
export type QuestionResponseKind = 'answered' | 'dismissed';

export interface QuestionResponse {
  kind: QuestionResponseKind;
  answer: string | null;
  responded_by_email: string;
  responded_at: string;
}

// One clarifying question Merlin is asking, attached to its initiative.
export interface ClarifyingQuestion {
  initiative_key: string;
  title: string;
  owner: string | null;
  people: string[];
  teams: string[];
  owner_team?: string | null;
  related_teams?: string[];
  question: string;
  /** Who the question names before its colon, canonicalised; the initiative's
   * people when it names nobody. The tab files the card under the first one. */
  addressed_to?: string[];
  response?: QuestionResponse | null;
  /** The quoted excerpts that name this question (contract v8): what it rests on. */
  evidence?: InitiativeEvidence[];
  /** v10: the initiative's mandate area key. */
  mandate_area?: string | null;
  /** The pilot group's relevant / not-relevant marks (kanban 3709db93). */
  relevance?: QuestionRelevance | null;
}

// A group-level question or an unplaced item (no initiative) with any pending response.
export interface GroupQuestion {
  question: string;
  response?: QuestionResponse | null;
  relevance?: QuestionRelevance | null;
}

// Identity of a question — the exact text shown, plus its initiative (null for
// group-level questions and unplaced items). Questions carry no ids.
export interface QuestionIdentity {
  initiative_key: string | null;
  question: string;
}

export interface QuestionResponseRequest extends QuestionIdentity {
  kind: QuestionResponseKind;
  answer?: string | null;
}

// One named gap from the deterministic integrity audit
// (services/roadmap/integrity.py) — the list Merlin's next run is handed.
export type IntegrityGapKind =
  | 'at_risk'
  | 'blocked_without_blocker'
  | 'dependency_in_prose'
  | 'no_target'
  | 'no_owner'
  | 'stale'
  | 'finished_blocker'
  | 'no_team'
  | 'no_mandate_area'
  | 'parked_without_reason';

export interface IntegrityGap {
  initiative_key: string;
  title: string;
  people: string[];
  kind: IntegrityGapKind;
  detail: string;
}

export interface IntegrityReport {
  as_of: string; // ISO date
  active_count: number;
  clean_count: number;
  summary: Record<string, number>;
  gaps: IntegrityGap[];
}

export interface ClarifyingQuestionsResponse {
  items: ClarifyingQuestion[];
  group_questions: GroupQuestion[];
  unplaced: GroupQuestion[];
  report_id: string | null;
  report_date: string | null;
}

// ---------------------------------------------------------------------------
// People directory + Merlin's-questions digest emails (kanban bafe8216).
// Backend: /admin/roadmap/people, /admin/roadmap/questions/digest/*.
// ---------------------------------------------------------------------------

export type EmailSource = 'directory' | 'portal';

export type DigestFrequency = 'every_run' | 'weekly' | 'off';

export interface RoadmapPerson {
  name: string;
  email: string | null;
  email_source: EmailSource | null;
  leads: string[];
  digest_frequency: DigestFrequency;
  /** Org chart (kanban b9e6e6af). */
  reports_to?: string | null;
  title?: string | null;
  member_of?: string[];
  top_of_chart?: boolean;
  open_questions: number;
  set_by_email?: string | null;
  set_at?: string | null;
  /** The words a Merlin-made change rests on (null for an admin's edit). */
  set_basis?: string | null;
}

export interface RoadmapTeamLead {
  team: string;
  lead: string | null;
  open_questions: number;
}

export interface RoadmapPeopleResponse {
  people: RoadmapPerson[];
  teams: RoadmapTeamLead[];
}

export interface RoadmapPersonRequest {
  email: string | null;
  leads: string[];
  /** Omit to keep the person's current preference. */
  digest_frequency?: DigestFrequency;
  /** Org chart: each field is applied only when present (null clears). */
  reports_to?: string | null;
  title?: string | null;
  member_of?: string[];
  top_of_chart?: boolean;
}

export interface OrgNode {
  name: string;
  title: string | null;
  reports_to: string | null;
  top_of_chart: boolean;
  leads: string[];
  member_of: string[];
  email: string | null;
  source: 'directory' | 'roadmap';
  set_by_email: string | null;
  set_basis: string | null;
  children: OrgNode[];
}

export interface OrgChartResponse {
  roots: OrgNode[];
  unplaced: OrgNode[];
  names: string[];
}

export type DigestKind = 'lead' | 'personal';

export interface DigestQuestion {
  initiative_key: string | null;
  title: string;
  teams: string[];
  addressed_to: string[];
  question: string;
}

export interface DigestPreview {
  kind: DigestKind;
  recipient_name: string;
  recipient_email: string | null;
  teams: string[];
  leads: string[];
  subject: string;
  /** The Markdown source; body_text / body_html are rendered from it. */
  body_markdown: string;
  body_text: string;
  body_html: string;
  questions: DigestQuestion[];
}

export interface DigestLedgerRow {
  kind: DigestKind;
  recipient_name: string;
  recipient_email: string | null;
  status: 'pending' | 'sent' | 'skipped' | 'failed';
  detail: string | null;
  questions: number;
  triggered_by: string;
  created_at: string;
  /** Replies handled on this digest's email thread. */
  replies?: number;
  /** Earlier attempts on this row (a retry or resend archived them). */
  attempts?: number;
}

export interface DigestPreviewResponse {
  report_id: string | null;
  report_date: string | null;
  enabled: boolean;
  digests: DigestPreview[];
  teams_without_lead: string[];
  already: DigestLedgerRow[];
}

export interface DigestSendResponse {
  report_id: string;
  sent: number;
  skipped: number;
  failed: number;
  already: number;
  rows: DigestLedgerRow[];
}

// One row of the roadmap source ledger (roadmap_source_ledger, kanban 83428bab):
// what a run was handed or cited, and what became of it — GET /admin/roadmap/sources.
export type RoadmapSourceKind = 'meeting' | 'email_thread' | 'document' | 'notes';
export type RoadmapSourceOutcome = 'unread' | 'read' | 'skipped' | 'deferred';

// What the small-model triage said about a meeting before any run listed it
// (roadmap_meeting_triage, kanban 57c26211). null = not judged / not a meeting.
export interface RoadmapSourceTriage {
  state: 'pending' | 'judged' | 'failed';
  verdict: 'wake' | 'hold' | null;
  reason: string | null;
  extract: string | null;
  confidence: number | null;
  judged_at: string | null;
}

export interface RoadmapSource {
  id: string;
  source_kind: RoadmapSourceKind;
  source_id: string;
  label: string | null; // a meeting's title when the ledger row is a meeting
  started_at: string | null;
  outcome: RoadmapSourceOutcome;
  reason: string | null;
  initiative_keys: string[];
  first_seen_at: string;
  first_seen_run_id: string | null;
  consumed_by_run_id: string | null;
  last_run_id: string | null;
  updated_at: string;
  triage?: RoadmapSourceTriage | null;
}

export interface RoadmapSourceListResponse {
  items: RoadmapSource[];
  total: number;
  limit: number;
  offset: number;
  summary: Record<string, number>;
}

export interface RoadmapSourceListParams {
  outcome?: RoadmapSourceOutcome;
  kind?: RoadmapSourceKind;
  limit?: number;
  offset?: number;
}

// One speaker turn of a transcript, as the source viewer prints it.
export interface SourceSegment {
  speaker: string | null;
  text: string;
  start: number | null; // seconds into the recording
}

// One source's words for the evidence viewer (GET /admin/roadmap/sources/{meeting|email}/{id}):
// a meeting or notes row with its transcript and segments, or an email's text.
export interface SourceDocument {
  source_kind: RoadmapSourceKind;
  source_id: string;
  title: string | null;
  started_at: string | null;
  origin: string | null; // the delivering system, or the email's sender
  text: string;
  segments: SourceSegment[];
  external_url: string | null;
}

// Which source the viewer opens: the kind and id a piece of evidence names,
// plus the quote to land on. `meeting` and `notes` share the meeting endpoint.
export interface SourceRef {
  kind: RoadmapSourceKind;
  id: string;
  quote?: string | null;
}

const BASE = '/admin/roadmap/reports';
const INITIATIVES_BASE = '/admin/roadmap/initiatives';

// ── Last week → now (GET /admin/roadmap/overview; services/roadmap/overview.py) ──

export type OverviewDelta =
  | 'finished' | 'sub_action_done' | 'slipped' | 'pulled_in' | 'dated' | 'blocked'
  | 'unblocked' | 'dropped' | 'status' | 'next_changed' | 'new' | 'unchanged';

export type SetOutOutcome = 'done' | 'still_open' | 'moved' | 'dropped' | 'gone';
export type SetOutKind = 'sub_action' | 'initiative';

export interface ReportStamp {
  id: string;
  report_date: string;
}

export interface OverviewSnapshot {
  status: InitiativeStatus;
  target_date: string | null;
  next_action: string | null;
  next_target: string | null;
  external_blocker: string | null;
}

export interface OverviewRow {
  initiative_key: string;
  title: string;
  teams: string[];
  owner: string | null;
  parked: boolean;
  /** null when the initiative was not in the earlier report (or there is none). */
  then: OverviewSnapshot | null;
  now: OverviewSnapshot;
  deltas: OverviewDelta[];
  actions_done: string[];
  open_questions: number;
}

/** Something the earlier report said was due by the latest report's date, with its outcome now. */
export interface SetOutItem {
  kind: SetOutKind;
  initiative_key: string;
  initiative_title: string;
  teams: string[];
  /** The sub-action key, or the initiative key for an initiative. */
  key: string;
  title: string;
  people: string[];
  target_then: string;
  target_now: string | null;
  outcome: SetOutOutcome;
  note_now: string;
}

export interface OverviewCounts {
  moved: number; slipped: number; blocked: number; new: number; unchanged: number;
  set_out: number; set_out_done: number; set_out_still_open: number; set_out_moved: number; set_out_dropped: number; set_out_gone: number;
  /** Open sub-actions in the earlier report that carried no target date. */
  undated_open: number;
}

export interface RoadmapOverview {
  then_report: ReportStamp | null;
  now_report: ReportStamp;
  active: number;
  counts: OverviewCounts;
  rows: OverviewRow[];
  set_out: SetOutItem[];
}

export const roadmapReportsApi = {
  // Request an on-demand run of the roadmap agent (admin). The backend writes
  // the run-ledger row first; a press while one is in flight returns that run.
  run: async (): Promise<RoadmapRunQueued> => {
    const r = await api.post<RoadmapRunQueued>(`${BASE}/run`);
    return r.data;
  },

  // The run ledger, newest first — every press / scheduled fire and its outcome.
  listRuns: async (limit = 10): Promise<RoadmapRun[]> => {
    const r = await api.get<{ items: RoadmapRun[] }>('/admin/roadmap/runs', {
      params: { limit },
    });
    return r.data.items;
  },

  list: async (
    params?: RoadmapReportListParams,
  ): Promise<RoadmapReportListResponse> => {
    const r = await api.get<RoadmapReportListResponse>(BASE, { params });
    return r.data;
  },

  // The machine-readable roadmap behind one report (404 → null: legacy
  // reports delivered before the structured contract carry none).
  getStructure: async (id: string): Promise<RoadmapStructure | null> => {
    try {
      const r = await api.get<RoadmapStructure>(`${BASE}/${id}/structure`);
      return r.data;
    } catch {
      return null;
    }
  },

  // Last week → now (owner ask 2026-10-12): the earlier report's state beside
  // the latest one, and last report's open sub-actions with their state now.
  // 404 before the first structured report → null.
  getOverview: async (sinceDays = 7): Promise<RoadmapOverview | null> => {
    try {
      const r = await api.get<RoadmapOverview>('/admin/roadmap/overview', { params: { since_days: sinceDays } });
      return r.data;
    } catch {
      return null;
    }
  },

  // CURRENT STATE of every tracked initiative (durable roadmap_initiatives
  // rows the runs update) — the Gantt view's data source.
  listInitiatives: async (): Promise<InitiativeStateListResponse> => {
    const r = await api.get<InitiativeStateListResponse>(INITIATIVES_BASE);
    return r.data;
  },

  // How one initiative changed over time (per-run field diffs).
  getInitiativeHistory: async (
    key: string,
  ): Promise<InitiativeHistoryResponse> => {
    const r = await api.get<InitiativeHistoryResponse>(
      `${INITIATIVES_BASE}/${encodeURIComponent(key)}/history`,
    );
    return r.data;
  },

  // Set the owning team and the related teams of an initiative (admin, v9).
  // The assignment replaces what is there; the server pins it so later
  // roadmap runs keep it.
  setInitiativeTeams: async (key: string, assignment: TeamAssignmentInput): Promise<InitiativeTeamsResult> => {
    const r = await api.put<InitiativeTeamsResult>(
      `${INITIATIVES_BASE}/${encodeURIComponent(key)}/teams`,
      assignment,
    );
    return r.data;
  },

  // Hand the team back to Merlin: drop the pin (current teams stay).
  releaseInitiativeTeams: async (key: string): Promise<void> => {
    await api.delete(`${INITIATIVES_BASE}/${encodeURIComponent(key)}/teams`);
  },

  // Park an initiative with a reason (admin). The server pins it so later
  // roadmap runs keep it parked.
  parkInitiative: async (key: string, reason: string | null): Promise<InitiativeParkResult> => {
    const r = await api.put<InitiativeParkResult>(
      `${INITIATIVES_BASE}/${encodeURIComponent(key)}/park`,
      { reason },
    );
    return r.data;
  },

  // Un-park: drop the pin and hand the initiative back to its earlier status.
  unparkInitiative: async (key: string): Promise<InitiativeParkResult> => {
    const r = await api.delete<InitiativeParkResult>(
      `${INITIATIVES_BASE}/${encodeURIComponent(key)}/park`,
    );
    return r.data;
  },

  // Recent changes across every initiative, newest first, stamped by run.
  // `since` (ISO instant) and `field` (a changed field's name, e.g. 'status')
  // narrow the feed server-side — the "completed since" view reads status
  // flips over a window that the newest-300 page would not cover.
  listEvents: async (limit = 300, opts: RoadmapEventListParams = {}): Promise<RoadmapEventListResponse> => {
    const params: Record<string, string | number> = { limit };
    if (opts.since) params.since = opts.since;
    if (opts.field) params.field = opts.field;
    const r = await api.get<RoadmapEventListResponse>('/admin/roadmap/events', { params });
    return r.data;
  },

  // Merlin's pending clarifying questions + the "isn't sure" unplaced items.
  listQuestions: async (): Promise<ClarifyingQuestionsResponse> => {
    const r = await api.get<ClarifyingQuestionsResponse>('/admin/roadmap/questions');
    return r.data;
  },

  // Answer or dismiss one of Merlin's questions in the UI. The next roadmap
  // run is handed every pending response; 404 when Merlin no longer asks it.
  respondToQuestion: async (req: QuestionResponseRequest): Promise<QuestionResponse> => {
    const r = await api.post<QuestionResponse>('/admin/roadmap/questions/respond', {
      initiative_key: req.initiative_key,
      question: req.question,
      kind: req.kind,
      answer: req.kind === 'answered' ? req.answer ?? null : null,
    });
    return r.data;
  },

  // Undo a pending response before a run consumes it.
  withdrawResponse: async (id: QuestionIdentity): Promise<void> => {
    await api.delete('/admin/roadmap/questions/respond', {
      params: { initiative_key: id.initiative_key ?? undefined, question: id.question },
    });
  },

  // Mark one of Merlin's questions relevant or not (any roadmap reader; one
  // mark per reader per question, marking again flips it).
  markRelevance: async (req: RelevanceMarkRequest): Promise<QuestionRelevance> => {
    const r = await api.post<QuestionRelevance>('/admin/roadmap/questions/relevance', req);
    return r.data;
  },

  // Clear the reader's own mark.
  clearRelevance: async (id: QuestionIdentity): Promise<void> => {
    await api.delete('/admin/roadmap/questions/relevance', {
      params: { initiative_key: id.initiative_key ?? undefined, question: id.question },
    });
  },

  // The relevance rate overall, per run and per team (null when it cannot load).
  getRelevanceSummary: async (): Promise<RelevanceSummary | null> => {
    try {
      const r = await api.get<RelevanceSummary>('/admin/roadmap/questions/relevance/summary');
      return r.data;
    } catch {
      return null;
    }
  },

  // Every task force's mandate areas, with how much work sits under each.
  listMandates: async (): Promise<TeamMandateListResponse> => {
    const r = await api.get<TeamMandateListResponse>('/admin/roadmap/mandates');
    return r.data;
  },

  // Set (replace) a task force's mandate areas (admin).
  setMandate: async (team: string, req: TeamMandateRequest): Promise<TeamMandate> => {
    const r = await api.put<TeamMandate>(`/admin/roadmap/mandates/${encodeURIComponent(team)}`, req);
    return r.data;
  },

  // The deterministic gap audit — what Merlin is asked to close next run.
  // The source ledger: what Merlin was handed and what became of each source.
  listSources: async (params: RoadmapSourceListParams = {}): Promise<RoadmapSourceListResponse> => {
    const r = await api.get<RoadmapSourceListResponse>('/admin/roadmap/sources', { params });
    return r.data;
  },

  // The words behind a quoted excerpt: a meeting's transcript or an email's
  // text, for the viewer the evidence links open (kanban 9eb5f005).
  getSourceDocument: async (ref: SourceRef): Promise<SourceDocument> => {
    const path = ref.kind === 'email_thread' ? 'email' : 'meeting';
    const r = await api.get<SourceDocument>(`/admin/roadmap/sources/${path}/${encodeURIComponent(ref.id)}`);
    return r.data;
  },

  getIntegrity: async (): Promise<IntegrityReport> => {
    const r = await api.get<IntegrityReport>('/admin/roadmap/integrity');
    return r.data;
  },

  // Everyone the roadmap names, where each digest would go, who leads each team.
  listPeople: async (): Promise<RoadmapPeopleResponse> => {
    const r = await api.get<RoadmapPeopleResponse>('/admin/roadmap/people');
    return r.data;
  },

  // Set a person's digest address and the teams they lead (admin).
  setPerson: async (name: string, req: RoadmapPersonRequest): Promise<RoadmapPerson> => {
    const r = await api.put<RoadmapPerson>(`/admin/roadmap/people/${encodeURIComponent(name)}`, req);
    return r.data;
  },

  // The org chart Merlin maintains: trees by reporting line + the unplaced.
  getOrgChart: async (): Promise<OrgChartResponse> => {
    const r = await api.get<OrgChartResponse>('/admin/roadmap/org');
    return r.data;
  },

  // What the digest emails would say right now — nothing is sent.
  previewDigests: async (): Promise<DigestPreviewResponse> => {
    const r = await api.get<DigestPreviewResponse>('/admin/roadmap/questions/digest/preview');
    return r.data;
  },

  // Email the outstanding questions now (admin); once per recipient per report.
  /** Send every digest still owed, or — with `one` — just that recipient's (a resend if already sent). */
  sendDigests: async (one?: { kind: DigestKind; recipient_name: string }): Promise<DigestSendResponse> => {
    const r = await api.post<DigestSendResponse>('/admin/roadmap/questions/digest/send', one);
    return r.data;
  },

  // The SPA path a feed entry links to — the shared report viewer.
  viewPath: (id: string): string => `/signals/reports/${id}`,
};
